import { ReactNode } from "react";
import { cn } from "src/lib/utils/utils";

/**
 * The small set of pieces the new CBT screens are built from.
 *
 * They mirror the `ui-x` primitives the other Droomwork front-ends share —
 * same navy and orange, same `rounded-2xl` card, same orange eyebrow.
 *
 * Deliberately small. The app is three screens; a full component library would
 * be more scaffolding than the thing it holds up.
 */

/** Page frame: the off-white ground and the centred column every screen uses. */
export const Shell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className="min-h-screen bg-surface font-sans text-brand-navy antialiased">
    <div className={cn("mx-auto w-full max-w-3xl px-5 py-10 sm:px-8", className)}>
      {children}
    </div>
  </div>
);

/** House card. */
export const Card = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "rounded-2xl border border-border bg-card p-6 shadow-ui-sm sm:p-8",
      className
    )}
  >
    {children}
  </div>
);

/** The signature orange uppercase label that sits above a title. */
export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
    {children}
  </div>
);

export const Title = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <h1
    className={cn(
      "font-display text-2xl font-extrabold tracking-tight text-brand-navy sm:text-3xl",
      className
    )}
  >
    {children}
  </h1>
);

const BUTTON_BASE =
  "inline-flex h-11 items-center justify-center gap-2 rounded-lg px-6 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Orange is the single forward action on a screen — Start, Next, Submit —
 * and navy opens something heavier. That split is the house rule across the
 * other front-ends, so one orange button per screen is the ceiling.
 */
export const PrimaryButton = ({
  children,
  onClick,
  disabled,
  type = "button",
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      BUTTON_BASE,
      "bg-brand-orange text-white hover:bg-brand-orange-600",
      className
    )}
  >
    {children}
  </button>
);

export const OutlineButton = ({
  children,
  onClick,
  disabled,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      BUTTON_BASE,
      "border border-border bg-card text-brand-navy hover:border-brand-navy hover:bg-surface",
      className
    )}
  >
    {children}
  </button>
);

/**
 * A score or progress bar. `tone` exists because the same bar reports two
 * different things: how far through the test you are (navy, neutral) and how
 * well you scored (orange, a result worth looking at).
 */
export const Meter = ({
  value,
  tone = "navy",
  className,
  label,
}: {
  value: number;
  tone?: "navy" | "orange";
  className?: string;
  label?: string;
}) => {
  const pct = Math.max(0, Math.min(100, Math.round(value || 0)));

  return (
    <div
      className={cn("h-2 w-full overflow-hidden rounded-full bg-brand-navy-100", className)}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width] duration-500 ease-out",
          tone === "orange" ? "bg-brand-orange" : "bg-brand-navy"
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

/** A link back to the talent dashboard, which every screen offers. */
export const DashboardLink = ({ className }: { className?: string }) => (
  <a
    href={process.env.REACT_APP_TALENT_WEB_URL}
    className={cn(
      "text-sm font-semibold text-brand-navy underline-offset-4 transition-colors hover:text-brand-orange hover:underline",
      className
    )}
  >
    Go to dashboard
  </a>
);
